// apps/web/src/components/users/permission-matrix-estado.ts — redesenho de /admin/perfis.
// Regras puras da matriz de permissões: busca, filtro "só concedidas", contadores e
// tri-state por módulo, módulos abertos de início e detecção de alterações não salvas.
import type { GrupoPermissoes, ItemPermissao } from '../../lib/permissoes-catalogo';

export type Concessoes = Readonly<Record<string, boolean>>;

export type EstadoDoModulo = 'nenhum' | 'parcial' | 'todos';

export interface ContagemDoModulo {
  readonly concedidas: number;
  readonly total: number;
  readonly estado: EstadoDoModulo;
}

/** Minúsculas e sem acento, para a busca casar "permissao" com "permissão". */
export function normalizarBusca(texto: string): string {
  return texto.normalize('NFD').replace(/[̀-ͯ]/g, '').toLocaleLowerCase('pt-BR').trim();
}

/**
 * Filtra os grupos pela busca (rótulo da ação, descrição, chave ou nome do módulo) e, se pedido, só pelas
 * permissões concedidas. Grupos que ficam vazios saem da lista.
 */
export function filtrarPermissoes(
  grupos: readonly GrupoPermissoes[],
  busca: string,
  soConcedidas: boolean,
  concessoes: Concessoes,
): readonly GrupoPermissoes[] {
  const termo = normalizarBusca(busca);
  const casa = (grupo: GrupoPermissoes, item: ItemPermissao): boolean =>
    termo === '' || [item.rotuloAcao, item.descricao, item.chave, grupo.rotulo].some((texto) => normalizarBusca(texto).includes(termo));
  return grupos
    .map((grupo) => ({
      ...grupo,
      itens: grupo.itens.filter((item) => (!soConcedidas || concessoes[item.chave] === true) && casa(grupo, item)),
    }))
    .filter((grupo) => grupo.itens.length > 0);
}

/** Verdadeiro quando busca ou "só concedidas" estão escondendo algo. */
export function filtroAtivo(busca: string, soConcedidas: boolean): boolean {
  return soConcedidas || normalizarBusca(busca) !== '';
}

/** "4/9" e o estado do checkbox do módulo (nenhum, parcial ou todos). */
export function contagemDoModulo(itens: readonly Pick<ItemPermissao, 'chave'>[], concessoes: Concessoes): ContagemDoModulo {
  const concedidas = itens.filter((item) => concessoes[item.chave] === true).length;
  const total = itens.length;
  const estado: EstadoDoModulo = concedidas === 0 ? 'nenhum' : concedidas === total ? 'todos' : 'parcial';
  return { concedidas, total, estado };
}

/** Módulos que começam abertos: os que têm pelo menos uma permissão concedida. */
export function modulosAbertosIniciais(grupos: readonly GrupoPermissoes[], concessoes: Concessoes): ReadonlySet<string> {
  return new Set(grupos.filter((grupo) => grupo.itens.some((item) => concessoes[item.chave] === true)).map((grupo) => grupo.modulo));
}

/** Marca ou desmarca de uma vez as permissões informadas (ação "Marcar todos" do módulo). */
export function marcarTodas(concessoes: Concessoes, chaves: readonly string[], marcar: boolean): Record<string, boolean> {
  const proximas: Record<string, boolean> = { ...concessoes };
  for (const chave of chaves) proximas[chave] = marcar;
  return proximas;
}

/** Chaves cuja concessão difere do que está salvo no servidor, em ordem estável. */
export function chavesAlteradas(salvas: Concessoes, atuais: Concessoes): readonly string[] {
  const todas = new Set([...Object.keys(salvas), ...Object.keys(atuais)]);
  return [...todas].filter((chave) => (salvas[chave] === true) !== (atuais[chave] === true)).sort((a, b) => a.localeCompare(b, 'pt-BR'));
}

/** Descartar = voltar à cópia do que está salvo. */
export function descartarAlteracoes(salvas: Concessoes): Record<string, boolean> {
  return { ...salvas };
}

/** "1 alteração não salva" / "3 alterações não salvas". */
export function rotuloAlteracoes(quantidade: number): string {
  return quantidade === 1 ? '1 alteração não salva' : `${quantidade} alterações não salvas`;
}
